import React, { useRef } from "react";
import DatePicker from "react-datepicker";
import { animate, useReducedMotion } from "framer-motion";

import { DURATION, EASE_OUT } from "../../motion";
import { ChevronRightIcon } from "../../icons";
import { cn } from "../../../utils/classnames";
import { AnimatedCalendarContainer, playSelectedRing } from "./formMotion";
import "./AnimatedDatePicker.css";

// How far the day grid slides when the month changes, in px.
const MONTH_SLIDE_DISTANCE = 16;

/** Months between two dates, ignoring the day: Jan 31 -> Feb 1 is 1. */
const monthsBetween = (fromDate, toDate) =>
  (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());

/** "en" / "ja" as passed to react-datepicker -> a locale for Intl dates. */
const toIntlLocale = (pickerLocale) => (pickerLocale === "ja" ? "ja-JP" : "en-US");

/**
 * The calendar's header: the year in small gold capitals above the month in
 * the serif display face, with round previous / next buttons on the right.
 * react-datepicker passes the month on screen and the navigation callbacks.
 */
const CalendarHeader = ({
  monthDate,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  pickerLocale,
}) => {
  const intlLocale = toIntlLocale(pickerLocale);
  const monthName = monthDate.toLocaleDateString(intlLocale, { month: "long" });
  // Japanese writes the year as "2026年".
  const year = pickerLocale === "ja" ? `${monthDate.getFullYear()}年` : monthDate.getFullYear();

  return (
    <div className="tlf-picker-header">
      <div className="tlf-picker-header__title" aria-live="polite">
        <span className="tlf-picker-header__year">{year}</span>
        <span className="tlf-picker-header__month">{monthName}</span>
      </div>
      <div className="tlf-picker-header__nav">
        <button
          type="button"
          className="tlf-picker-nav-button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          aria-label="Previous month"
        >
          <ChevronRightIcon width={6} height={9} color="#282828" style={{ transform: "rotate(180deg)" }} />
        </button>
        <button
          type="button"
          className="tlf-picker-nav-button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          aria-label="Next month"
        >
          <ChevronRightIcon width={6} height={9} color="#282828" />
        </button>
      </div>
    </div>
  );
};

/**
 * "14:05" -> a Date today at 14:05, or null when the time is empty or not
 * typed in full yet. The time picker needs a Date to highlight the chosen time.
 */
const timeStringToDate = (timeString) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(timeString || "");
  if (!match) return null;
  const date = new Date();
  date.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return date;
};

/** Full hours (13:00, 14:00) get a class so the time list is easy to scan. */
const getTimeClassName = (time) => (time.getMinutes() === 0 ? "tlf-time--full-hour" : "");

/**
 * A drop-in replacement for react-datepicker's <DatePicker> with motion:
 *
 * - the popup fades and drops in (AnimatedCalendarContainer), and the time
 *   list glides to the current time instead of jumping there;
 * - changing month slides the day grid in from the side you are heading
 *   (next month from the right, previous month from the left);
 * - picking a date or a time from the popup flashes the same gold ring as
 *   the location field, so the choice visibly lands in the input.
 *
 * It also gives both pickers the TLF look (AnimatedDatePicker.css): a floating
 * white card, serif month title, round day cells, a black selected day / time
 * and a gold dot for today.
 *
 * Every prop is passed straight through, so swapping <DatePicker> for
 * <AnimatedDatePicker> changes nothing about how the picker behaves. One extra
 * prop: `selectedTime` ("HH:mm") lets a time picker highlight and scroll to
 * the chosen time when the form keeps the time as text, not a Date.
 */
const AnimatedDatePicker = ({ onSelect, onChange, onMonthChange, selectedTime, ...datePickerProps }) => {
  const shouldReduceMotion = useReducedMotion();

  // The popup is rendered next to the input (no portal), so both live inside
  // this wrapper and can be found from it.
  const wrapperRef = useRef(null);

  // The month the calendar is showing, to tell which way the next change goes.
  const shownMonthRef = useRef(null);

  const findInWrapper = (selector) => wrapperRef.current?.querySelector(selector);

  const flashInput = () => playSelectedRing(findInWrapper("input"), shouldReduceMotion);

  const slideMonth = (newMonthDate) => {
    const previousMonthDate = shownMonthRef.current || datePickerProps.selected || new Date();
    shownMonthRef.current = newMonthDate;

    const direction = Math.sign(monthsBetween(previousMonthDate, newMonthDate));
    if (direction === 0 || shouldReduceMotion) return;

    // Wait one frame so the new month's days are on screen before they move.
    requestAnimationFrame(() => {
      const dayGrid = findInWrapper(".react-datepicker__month");
      const monthTitle = findInWrapper(".tlf-picker-header__title");
      const slideTransition = { duration: DURATION.fast, ease: EASE_OUT };
      if (dayGrid) {
        animate(dayGrid, { opacity: [0, 1], x: [direction * MONTH_SLIDE_DISTANCE, 0] }, slideTransition);
      }
      if (monthTitle) {
        animate(monthTitle, { opacity: [0, 1] }, slideTransition);
      }
    });
  };

  const handleSelect = (date, event) => {
    // Time-only pickers flash from handleChange instead.
    if (!datePickerProps.showTimeSelectOnly) flashInput();
    onSelect?.(date, event);
  };

  const handleChange = (date, event) => {
    // No event means the time was clicked in the list; typing into the field
    // also calls onChange, and that shouldn't flash on every keystroke.
    const isPickedFromPopup = !event || event.type === "click";
    if (isPickedFromPopup && datePickerProps.showTimeSelectOnly) flashInput();
    onChange?.(date, event);
  };

  const handleMonthChange = (date) => {
    slideMonth(date);
    onMonthChange?.(date);
  };

  const isTimeOnly = Boolean(datePickerProps.showTimeSelectOnly);

  return (
    <div ref={wrapperRef}>
      <DatePicker
        calendarContainer={AnimatedCalendarContainer}
        timeClassName={getTimeClassName}
        selected={selectedTime !== undefined ? timeStringToDate(selectedTime) : undefined}
        {...datePickerProps}
        calendarClassName={cn("tlf-picker", datePickerProps.locale === "ja" && "tlf-picker-ja", datePickerProps.calendarClassName)}
        popperClassName={cn("tlf-picker-popper", datePickerProps.popperClassName)}
        renderCustomHeader={
          isTimeOnly
            ? undefined
            : (headerProps) => <CalendarHeader {...headerProps} pickerLocale={datePickerProps.locale} />
        }
        onSelect={handleSelect}
        // react-datepicker requires onChange, so always pass one.
        onChange={handleChange}
        onMonthChange={handleMonthChange}
        onCalendarOpen={() => {
          shownMonthRef.current = null;
          datePickerProps.onCalendarOpen?.();
        }}
      />
    </div>
  );
};

export default AnimatedDatePicker;
